<script>
import callsApiService from '@/services/callsApiService'
import { Device } from '@twilio/voice-sdk'
import { markRaw } from 'vue'
import { useDialerStore } from '@/stores/dialerStore'

export default {
  setup() {
    const dialerStore = useDialerStore()
    return { dialerStore }
  },
  data() {
    return {
      dialedNumber: '',
      callNotes: '',
      activeTab: 'dialer',
      currentCallLogId: null,
      isLoading: false,
      twilioDevice: null,
      currentCall: null,
      callStatus: 'idle', // idle, connecting, ringing, in-call, ended
      incomingCall: null,
      incomingCallFrom: '',
      callerInfo: null,
      loadingCallerInfo: false,
    }
  },
  computed: {
    show: {
      get() {
        return this.dialerStore.show
      },
      set(value) {
        if (value) {
          this.dialerStore.openDialer(this.dialerStore.contact)
        } else {
          this.dialerStore.closeDialer()
        }
      }
    },
    contact() {
      return this.dialerStore.contact
    }
  },
  watch: {
    'dialerStore.show'(val) {
      if (val && this.contact) {
        this.dialedNumber = this.contact.phone || this.contact.phone_number || ''
      }
    }
  },
  async mounted() {
    // Initialize Twilio Device when component mounts (keep it running in background)
    await this.initializeTwilioDevice()
  },
  methods: {
    async initializeTwilioDevice() {
      if (this.twilioDevice) return // Already initialized

      try {
        // Get access token from backend
        console.log('Fetching Twilio access token...')
        const response = await callsApiService.getAccessToken()
        console.log('Access token response:', response.data)

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to get access token')
        }

        const token = response.data.data.token
        console.log('Initializing Twilio Device...')

        // Initialize Twilio Device (markRaw prevents Vue reactivity issues)
        this.twilioDevice = markRaw(new Device(token, {
          logLevel: 1,
          codecPreferences: ['opus', 'pcmu'],
        }))

        // Set up event listeners
        this.twilioDevice.on('registered', () => {
          console.log('Twilio Device Ready - Can receive calls')
        })

        // Proactively refresh token before it expires
        this.twilioDevice.on('tokenWillExpire', async () => {
          console.log('Twilio token will expire soon, refreshing...')
          await this.refreshTwilioToken()
        })

        this.twilioDevice.on('error', async (error) => {
          console.error('Twilio Device Error:', error)

          // Handle expired token - refresh and re-register
          if (error.code === 20104 || error.message?.includes('expired')) {
            console.log('Token expired, refreshing...')
            await this.refreshTwilioToken()
            return
          }

          alert(`Device Error: ${error.message}`)
        })

        // Listen for incoming calls
        this.twilioDevice.on('incoming', (call) => {
          console.log('Incoming call received:', call)
          this.incomingCall = markRaw(call)
          this.incomingCallFrom = call.parameters.From || 'Unknown'
          this.callStatus = 'ringing'

          // Fetch caller information
          this.fetchCallerInfo(this.incomingCallFrom)

          // Set up call event listeners immediately (before accepting)
          const handleCallEnd = (eventName) => {
            console.log(`Call event fired: ${eventName}`)
            this.resetCallState()

            // Auto-close modal after call ends
            setTimeout(() => {
              if (this.callStatus === 'idle') {
                this.show = false
              }
            }, 2000)
          }

          call.on('disconnect', () => handleCallEnd('disconnect'))
          call.on('cancel', () => handleCallEnd('cancel'))
          call.on('reject', () => handleCallEnd('reject'))
          call.on('error', (error) => {
            console.log('Call error:', error)
            handleCallEnd('error')
          })

          console.log('Event listeners attached to incoming call')

          // Auto-open the modal to show incoming call
          this.show = true

          // Show incoming call notification
          console.log(`Incoming call from ${this.incomingCallFrom}`)
        })

        // Register the device
        await this.twilioDevice.register()
      } catch (error) {
        console.error('Failed to initialize Twilio Device:', error)
        // Don't show alert for 401/403 errors - handled by axios interceptor
        const status = error?.response?.status
        if (status !== 401 && status !== 403) {
          const errorMsg = error?.message || error?.toString() || 'Unknown error'
          alert(`Failed to initialize calling: ${errorMsg}`)
        }
      }
    },

    async refreshTwilioToken() {
      try {
        console.log('Fetching new Twilio access token...')
        const response = await callsApiService.getAccessToken()

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to get access token')
        }

        const newToken = response.data.data.token
        console.log('Got new token, updating device...')

        // Update the device with the new token
        if (this.twilioDevice) {
          this.twilioDevice.updateToken(newToken)
          console.log('Twilio token refreshed successfully')
        }
      } catch (error) {
        console.error('Failed to refresh Twilio token:', error)
        // Re-initialize the device if token refresh fails
        this.twilioDevice = null
        await this.initializeTwilioDevice()
      }
    },

    cleanupTwilioDevice() {
      // Only cleanup calls, not the device itself (keep it running for incoming calls)
      if (this.incomingCall) {
        this.incomingCall.reject()
        this.incomingCall = null
      }
      if (this.currentCall) {
        this.currentCall.disconnect()
        this.currentCall = null
      }
    },
    destroyTwilioDevice() {
      // Full cleanup when component is destroyed
      this.cleanupTwilioDevice()
      if (this.twilioDevice) {
        this.twilioDevice.destroy()
        this.twilioDevice = null
      }
    },
    getUserInitials(name) {
      if (!name) return '??'
      const parts = name.trim().split(' ').filter(p => p.length > 0)
      if (parts.length >= 2 && parts[0][0] && parts[1][0]) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      if (parts.length > 0 && parts[0].length >= 2) {
        return parts[0].substring(0, 2).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    getAvatarUrl(avatarPath) {
      if (!avatarPath) return null
      const baseUrl = 'https://happipad.sfo3.digitaloceanspaces.com'
      return `${baseUrl}/avatars/${avatarPath}`
    },
    appendDigit(digit) {
      // If we're in an active call, send the digit as a DTMF tone
      if (this.callStatus === 'in-call' && this.currentCall) {
        this.currentCall.sendDigits(digit)
        console.log('Sent DTMF tone:', digit)
      } else {
        // Otherwise, add it to the dialed number
        this.dialedNumber += digit
      }
    },
    deleteDigit() {
      this.dialedNumber = this.dialedNumber.slice(0, -1)
    },
    async makeCall() {
      if (!this.dialedNumber) {
        alert('Please enter a phone number')
        return
      }

      if (!this.twilioDevice) {
        alert('Calling device not ready. Please wait and try again.')
        return
      }

      this.isLoading = true
      this.callStatus = 'connecting'

      try {
        // Create call log in backend first (without initiating server call)
        const callLogResponse = await callsApiService.createBrowserCallLog({
          to_phone: this.dialedNumber,
          contact_user_id: this.contact?.id || null,
          contact_name: this.contact?.name || null
        })

        if (callLogResponse.data.success) {
          this.currentCallLogId = callLogResponse.data.data.call_log_id
          console.log('Call log created:', this.currentCallLogId)
        }

        // Make the call using Twilio Device
        const call = await this.twilioDevice.connect({
          params: {
            To: this.dialedNumber,
            CallLogId: this.currentCallLogId // Pass call log ID to webhook
          }
        })

        // Use markRaw to prevent Vue reactivity issues
        this.currentCall = markRaw(call)

        // Set up call event listeners
        call.on('accept', () => {
          console.log('Call accepted')
          this.callStatus = 'in-call'
          this.isLoading = false
          console.log('Call connected! You can now speak. Use the dial pad to send tones.')
        })

        call.on('disconnect', () => {
          console.log('Call disconnected')
          this.resetCallState()

          // Auto-close modal after disconnect
          setTimeout(() => {
            if (this.callStatus === 'idle') {
              this.show = false
            }
          }, 2000)
        })

        call.on('cancel', () => {
          console.log('Call canceled')
          this.resetCallState()

          // Auto-close modal after cancel
          setTimeout(() => {
            if (this.callStatus === 'idle') {
              this.show = false
            }
          }, 2000)
        })

        call.on('reject', () => {
          console.log('Call rejected')
          this.resetCallState()
          alert('Call was rejected')

          // Auto-close modal after rejection
          setTimeout(() => {
            if (this.callStatus === 'idle') {
              this.show = false
            }
          }, 2000)
        })

      } catch (error) {
        console.error('Error making call:', error)
        const errorMsg = error?.message || error?.toString() || 'Unknown error'
        alert(`Error making call: ${errorMsg}`)
        this.callStatus = 'idle'
        this.isLoading = false
      }
    },

    acceptIncomingCall() {
      if (!this.incomingCall) return

      console.log('Accepting incoming call')
      this.incomingCall.accept()
      this.currentCall = this.incomingCall
      this.callStatus = 'in-call'

      // Clear incoming call notification now that it's accepted
      // (event listeners already set up in 'incoming' handler)
      this.incomingCall = null
      this.incomingCallFrom = ''
    },

    resetCallState() {
      this.callStatus = 'idle'
      this.currentCall = null
      this.incomingCall = null
      this.incomingCallFrom = ''
      this.callerInfo = null
      this.loadingCallerInfo = false
      this.isLoading = false
    },

    async fetchCallerInfo(phoneNumber) {
      if (!phoneNumber || phoneNumber === 'Unknown') {
        this.callerInfo = null
        return
      }

      try {
        this.loadingCallerInfo = true
        console.log('Fetching caller info for:', phoneNumber)

        const response = await callsApiService.searchUserByPhone(phoneNumber)

        if (response.data.success && response.data.data) {
          this.callerInfo = response.data.data
          console.log('Caller info found:', this.callerInfo)

          // Auto-switch to caller info tab if data is found
          this.activeTab = 'caller-info'
        } else {
          this.callerInfo = null
          console.log('No caller info found for:', phoneNumber)
        }
      } catch (error) {
        console.error('Failed to fetch caller info:', error)
        this.callerInfo = null
      } finally {
        this.loadingCallerInfo = false
      }
    },

    rejectIncomingCall() {
      if (!this.incomingCall) return

      console.log('Rejecting incoming call')
      this.incomingCall.reject()
      this.incomingCall = null
      this.incomingCallFrom = ''
      this.callStatus = 'idle'
    },

    hangupCall() {
      console.log('Manual hangup triggered')
      if (this.currentCall) {
        this.currentCall.disconnect()
      }
      // Force reset state even if disconnect event doesn't fire
      this.resetCallState()

      // Close modal after a short delay
      setTimeout(() => {
        this.show = false
      }, 1000)
    },
    closeModal() {
      this.hangupCall()
      this.show = false
      this.dialedNumber = ''
      this.callNotes = ''
      this.currentCallLogId = null
      this.activeTab = 'dialer'
      this.callStatus = 'idle'
    },
    async saveNotes() {
      if (!this.callNotes.trim()) {
        alert('Please enter some notes before saving.')
        return
      }

      if (!this.currentCallLogId) {
        alert('Please make a call first before saving notes.')
        return
      }

      this.isLoading = true
      try {
        const response = await callsApiService.saveNotes(this.currentCallLogId, this.callNotes)

        if (response.data.success) {
          alert('Notes saved successfully!')
          this.closeModal()
        } else {
          alert(`Failed to save notes: ${response.data.message || 'Unknown error'}`)
        }
      } catch (error) {
        console.error('Error saving notes:', error)
        alert(`Error saving notes: ${error.response?.data?.message || error.message}`)
      } finally {
        this.isLoading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    }
  },
  beforeUnmount() {
    // Destroy Twilio Device when component is destroyed
    this.destroyTwilioDevice()
  }
}
</script>

<template>
  <BModal
    v-model="show"
    title="Phone Dialer"
    centered
    size="md"
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
    class="v-modal-custom"
  >
    <!-- Incoming Call Notification -->
    <div v-if="incomingCall" class="alert alert-info border-2 border-primary mb-4">
      <div class="d-flex align-items-center justify-content-between">
        <div class="flex-grow-1">
          <h5 class="mb-1">
            <i class="ri-phone-line me-2"></i>Incoming Call
          </h5>
          <p class="mb-0 text-muted">From: {{ incomingCallFrom }}</p>
        </div>
        <div class="d-flex gap-2">
          <BButton
            variant="success"
            size="sm"
            class="rounded-circle"
            style="width: 48px; height: 48px;"
            @click="acceptIncomingCall"
            title="Accept Call"
          >
            <i class="ri-phone-fill fs-5"></i>
          </BButton>
          <BButton
            variant="danger"
            size="sm"
            class="rounded-circle"
            style="width: 48px; height: 48px;"
            @click="rejectIncomingCall"
            title="Reject Call"
          >
            <i class="ri-phone-off-fill fs-5"></i>
          </BButton>
        </div>
      </div>
    </div>

    <!-- Contact Info -->
    <div v-if="contact" class="mb-4 text-center">
      <div class="avatar-md mx-auto mb-3">
        <img
          v-if="contact.avatar_path"
          :src="getAvatarUrl(contact.avatar_path)"
          alt="avatar"
          class="rounded-circle"
          style="width: 100%; height: 100%; object-fit: cover;"
        />
        <div
          v-else
          class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3"
        >
          {{ getUserInitials(contact.name) }}
        </div>
      </div>
      <h5 class="mb-1">{{ contact.name }}</h5>
      <p class="text-muted fs-13 mb-0">{{ contact.email }}</p>
    </div>

    <!-- Tabs -->
    <ul class="nav nav-tabs nav-justified mb-3" role="tablist">
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === 'dialer' }"
          @click="activeTab = 'dialer'"
          role="tab"
          style="cursor: pointer;"
        >
          <i class="ri-phone-line me-1"></i>Dialer
        </a>
      </li>
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === 'notes' }"
          @click="activeTab = 'notes'"
          role="tab"
          style="cursor: pointer;"
        >
          <i class="ri-file-text-line me-1"></i>Notes
        </a>
      </li>
      <li v-if="incomingCall || callerInfo" class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === 'caller-info' }"
          @click="activeTab = 'caller-info'"
          role="tab"
          style="cursor: pointer;"
        >
          <i class="ri-user-line me-1"></i>Caller Info
          <span v-if="callerInfo" class="badge bg-success ms-1">Found</span>
        </a>
      </li>
    </ul>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Dialer Tab -->
      <div v-show="activeTab === 'dialer'" class="text-center">
        <!-- Call Status Badge -->
        <div v-if="callStatus !== 'idle'" class="mb-3">
          <span
            class="badge fs-6 px-3 py-2"
            :class="{
              'bg-warning': callStatus === 'connecting',
              'bg-success': callStatus === 'in-call',
              'bg-secondary': callStatus === 'ended'
            }"
          >
            <i v-if="callStatus === 'connecting'" class="ri-loader-4-line"></i>
            <i v-if="callStatus === 'in-call'" class="ri-phone-line"></i>
            {{ callStatus === 'connecting' ? 'Connecting...' :
               callStatus === 'in-call' ? 'Call Active - Use keypad to send tones' :
               'Call Ended' }}
          </span>
        </div>

        <!-- Display Screen -->
        <div class="mb-4">
          <input
            type="text"
            class="form-control form-control-lg text-center fs-4"
            v-model="dialedNumber"
            placeholder="Enter number"
            readonly
          />
        </div>

        <!-- Dial Pad -->
        <div class="dial-pad mb-3">
          <BRow class="g-2">
            <BCol cols="4" v-for="digit in ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']" :key="digit">
              <BButton
                variant="light"
                class="w-100 py-3 fs-5 fw-medium"
                @click="appendDigit(digit)"
              >
                {{ digit }}
              </BButton>
            </BCol>
          </BRow>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex gap-2 justify-content-center">
          <!-- Delete button - only show when not in call -->
          <BButton
            v-if="callStatus !== 'in-call'"
            variant="danger"
            size="lg"
            class="rounded-circle"
            style="width: 60px; height: 60px;"
            @click="deleteDigit"
            title="Delete"
          >
            <i class="ri-delete-back-2-line fs-5"></i>
          </BButton>

          <!-- Call/Hangup button -->
          <BButton
            v-if="callStatus === 'idle' || callStatus === 'ended'"
            variant="success"
            size="lg"
            class="rounded-circle"
            style="width: 60px; height: 60px;"
            @click="makeCall"
            :disabled="isLoading"
            title="Call"
          >
            <i v-if="!isLoading" class="ri-phone-fill fs-5"></i>
            <span v-else class="spinner-border spinner-border-sm"></span>
          </BButton>
          <BButton
            v-else-if="callStatus === 'in-call' || callStatus === 'connecting'"
            variant="danger"
            size="lg"
            class="rounded-circle"
            style="width: 60px; height: 60px;"
            @click="hangupCall"
            title="Hang Up"
          >
            <i class="ri-phone-off-fill fs-5"></i>
          </BButton>

          <!-- Close button -->
          <BButton
            variant="secondary"
            size="lg"
            class="rounded-circle"
            style="width: 60px; height: 60px;"
            @click="closeModal"
            title="Close"
          >
            <i class="ri-close-line fs-5"></i>
          </BButton>
        </div>
      </div>

      <!-- Notes Tab -->
      <div v-show="activeTab === 'notes'">
        <div class="mb-3">
          <label for="callNotes" class="form-label fw-semibold">
            <i class="ri-file-text-line me-1"></i>Call Notes
          </label>
          <textarea
            id="callNotes"
            class="form-control"
            rows="8"
            v-model="callNotes"
            placeholder="Enter notes about this call..."
          ></textarea>
        </div>
        <div class="d-flex gap-2">
          <BButton
            variant="primary"
            @click="saveNotes"
            :disabled="isLoading"
            class="flex-grow-1"
          >
            <span v-if="!isLoading">
              <i class="ri-save-line me-1"></i>Save Notes
            </span>
            <span v-else>
              <span class="spinner-border spinner-border-sm me-1"></span>Saving...
            </span>
          </BButton>
          <BButton
            variant="secondary"
            @click="closeModal"
          >
            <i class="ri-close-line me-1"></i>Close
          </BButton>
        </div>
      </div>

      <!-- Caller Info Tab -->
      <div v-show="activeTab === 'caller-info'">
        <!-- Loading State -->
        <div v-if="loadingCallerInfo" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-3">Searching for caller information...</p>
        </div>

        <!-- Caller Found -->
        <div v-else-if="callerInfo" class="caller-info-content">
          <div class="alert alert-success border-0 mb-4" role="alert">
            <i class="ri-checkbox-circle-line me-2"></i>
            <strong>User Found!</strong> This caller is registered in the system.
          </div>

          <div class="card border">
            <div class="card-body">
              <!-- User Avatar and Name -->
              <div class="text-center mb-4">
                <div class="avatar-lg mx-auto mb-3">
                  <div class="avatar-title bg-primary-subtle text-primary rounded-circle fs-2">
                    {{ callerInfo.name ? callerInfo.name.charAt(0).toUpperCase() : '?' }}
                  </div>
                </div>
                <h4 class="mb-1">{{ callerInfo.name || 'Unknown' }}</h4>
                <p class="text-muted mb-0">
                  <span class="badge bg-info-subtle text-info">
                    {{ callerInfo.role === 'host' ? 'Host' : callerInfo.role === 'renter' ? 'Renter' : 'User' }}
                  </span>
                </p>
              </div>

              <!-- Contact Details -->
              <div class="table-responsive">
                <table class="table table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td class="fw-semibold text-muted" style="width: 40%;">
                        <i class="ri-user-line me-2"></i>Full Name
                      </td>
                      <td>{{ callerInfo.name || 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-muted">
                        <i class="ri-mail-line me-2"></i>Email
                      </td>
                      <td>
                        <a :href="`mailto:${callerInfo.email}`" class="text-decoration-none">
                          {{ callerInfo.email || 'N/A' }}
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-muted">
                        <i class="ri-phone-line me-2"></i>Phone
                      </td>
                      <td>{{ callerInfo.phone || 'N/A' }}</td>
                    </tr>
                    <tr v-if="callerInfo.client">
                      <td class="fw-semibold text-muted">
                        <i class="ri-building-line me-2"></i>Client
                      </td>
                      <td>{{ callerInfo.client.name }}</td>
                    </tr>
                    <tr v-if="callerInfo.address">
                      <td class="fw-semibold text-muted">
                        <i class="ri-map-pin-line me-2"></i>Address
                      </td>
                      <td>{{ callerInfo.address.full_address || 'N/A' }}</td>
                    </tr>
                    <tr v-if="callerInfo.background_check">
                      <td class="fw-semibold text-muted">
                        <i class="ri-shield-check-line me-2"></i>Background Check
                      </td>
                      <td>
                        <span class="badge" :class="{
                          'bg-success': callerInfo.background_check.status === 'approved',
                          'bg-warning': callerInfo.background_check.status === 'pending',
                          'bg-danger': callerInfo.background_check.status === 'rejected',
                          'bg-secondary': !callerInfo.background_check.status
                        }">
                          {{ callerInfo.background_check.status || 'N/A' }}
                        </span>
                        <small class="text-muted ms-2">
                          ({{ formatDate(callerInfo.background_check.check_date) }})
                        </small>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-muted">
                        <i class="ri-calendar-line me-2"></i>Member Since
                      </td>
                      <td>{{ formatDate(callerInfo.member_since) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- No Caller Info Found -->
        <div v-else class="text-center py-5">
          <div class="avatar-lg mx-auto mb-4">
            <div class="avatar-title bg-warning-subtle text-warning rounded-circle">
              <i class="ri-user-unfollow-line fs-2"></i>
            </div>
          </div>
          <h5 class="mb-2">No User Found</h5>
          <p class="text-muted mb-4">
            This phone number ({{ incomingCallFrom }}) is not registered in the system.
          </p>
          <div class="alert alert-info border-0" role="alert">
            <i class="ri-information-line me-2"></i>
            The caller might be a new contact or calling from an unregistered number.
          </div>
        </div>
      </div>
    </div>
  </BModal>
</template>

<style scoped>
.dial-pad .btn {
  transition: all 0.2s ease;
}

.dial-pad .btn:active {
  transform: scale(0.95);
}

.avatar-md {
  width: 80px;
  height: 80px;
}

.avatar-md .avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
